// 防刷管理工具
interface SpamRecord {
  count: number
  lastSendTime: number
  blockedUntil?: number
}

interface IPRecord {
  count: number
  lastSendTime: number
  blockedUntil?: number
}

class AntiSpamManager {
  private userRecords = new Map<string, SpamRecord>()
  private ipRecords = new Map<string, IPRecord>()
  
  // 配置参数
  private readonly CONFIG = {
    // 用户限制：每5分钟最多发送1次
    USER_LIMIT: {
      MAX_COUNT: 1,
      TIME_WINDOW: 5 * 60 * 1000, // 5分钟
      BLOCK_DURATION: 10 * 60 * 1000 // 10分钟封禁
    },
    // IP限制：每10分钟最多发送3次
    IP_LIMIT: {
      MAX_COUNT: 3,
      TIME_WINDOW: 10 * 60 * 1000, // 10分钟
      BLOCK_DURATION: 30 * 60 * 1000 // 30分钟封禁
    },
    // 全局限制：每小时最多发送10次
    GLOBAL_LIMIT: {
      MAX_COUNT: 10,
      TIME_WINDOW: 60 * 60 * 1000, // 1小时
      BLOCK_DURATION: 60 * 60 * 1000 // 1小时封禁
    }
  }

  // 生成用户标识（基于浏览器指纹）
  public generateUserFingerprint(): string {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('User fingerprint', 2, 2)
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|')
    
    // 简单哈希
    let hash = 0
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // 转换为32位整数
    }
    return Math.abs(hash).toString()
  }

  // 获取IP地址（简化版，实际项目中应该从服务器获取）
  public getClientIP(): string {
    // 这里使用一个简化的方法，实际项目中应该从服务器获取真实IP
    return 'client_' + this.generateUserFingerprint().slice(0, 8)
  }

  // 检查用户是否被限制
  private checkUserLimit(userId: string): { allowed: boolean, reason?: string, waitTime?: number } {
    const now = Date.now()
    const record = this.userRecords.get(userId)
    
    if (!record) {
      return { allowed: true }
    }

    // 检查是否在封禁期内
    if (record.blockedUntil && now < record.blockedUntil) {
      const waitTime = Math.ceil((record.blockedUntil - now) / 1000 / 60) // 分钟
      return { 
        allowed: false, 
        reason: `用户发送过于频繁，请等待 ${waitTime} 分钟后再试`,
        waitTime
      }
    }

    // 检查时间窗口内的发送次数
    if (now - record.lastSendTime < this.CONFIG.USER_LIMIT.TIME_WINDOW) {
      if (record.count >= this.CONFIG.USER_LIMIT.MAX_COUNT) {
        // 触发封禁
        record.blockedUntil = now + this.CONFIG.USER_LIMIT.BLOCK_DURATION
        const waitTime = Math.ceil(this.CONFIG.USER_LIMIT.BLOCK_DURATION / 1000 / 60)
        return { 
          allowed: false, 
          reason: `发送过于频繁，账户已被限制 ${waitTime} 分钟`,
          waitTime
        }
      }
    } else {
      // 重置计数
      record.count = 0
    }

    return { allowed: true }
  }

  // 检查IP是否被限制
  private checkIPLimit(ip: string): { allowed: boolean, reason?: string, waitTime?: number } {
    const now = Date.now()
    const record = this.ipRecords.get(ip)
    
    if (!record) {
      return { allowed: true }
    }

    // 检查是否在封禁期内
    if (record.blockedUntil && now < record.blockedUntil) {
      const waitTime = Math.ceil((record.blockedUntil - now) / 1000 / 60)
      return { 
        allowed: false, 
        reason: `IP地址发送过于频繁，请等待 ${waitTime} 分钟后再试`,
        waitTime
      }
    }

    // 检查时间窗口内的发送次数
    if (now - record.lastSendTime < this.CONFIG.IP_LIMIT.TIME_WINDOW) {
      if (record.count >= this.CONFIG.IP_LIMIT.MAX_COUNT) {
        // 触发封禁
        record.blockedUntil = now + this.CONFIG.IP_LIMIT.BLOCK_DURATION
        const waitTime = Math.ceil(this.CONFIG.IP_LIMIT.BLOCK_DURATION / 1000 / 60)
        return { 
          allowed: false, 
          reason: `IP地址发送过于频繁，已被限制 ${waitTime} 分钟`,
          waitTime
        }
      }
    } else {
      // 重置计数
      record.count = 0
    }

    return { allowed: true }
  }

  // 检查全局限制
  private checkGlobalLimit(): { allowed: boolean, reason?: string, waitTime?: number } {
    const now = Date.now()
    const globalKey = 'global'
    const record = this.userRecords.get(globalKey)
    
    if (!record) {
      return { allowed: true }
    }

    // 检查是否在封禁期内
    if (record.blockedUntil && now < record.blockedUntil) {
      const waitTime = Math.ceil((record.blockedUntil - now) / 1000 / 60)
      return { 
        allowed: false, 
        reason: `系统发送过于频繁，请等待 ${waitTime} 分钟后再试`,
        waitTime
      }
    }

    // 检查时间窗口内的发送次数
    if (now - record.lastSendTime < this.CONFIG.GLOBAL_LIMIT.TIME_WINDOW) {
      if (record.count >= this.CONFIG.GLOBAL_LIMIT.MAX_COUNT) {
        // 触发封禁
        record.blockedUntil = now + this.CONFIG.GLOBAL_LIMIT.BLOCK_DURATION
        const waitTime = Math.ceil(this.CONFIG.GLOBAL_LIMIT.BLOCK_DURATION / 1000 / 60)
        return { 
          allowed: false, 
          reason: `系统发送过于频繁，已被限制 ${waitTime} 分钟`,
          waitTime
        }
      }
    } else {
      // 重置计数
      record.count = 0
    }

    return { allowed: true }
  }

  // 记录发送
  public recordSend(userId: string, ip: string): void {
    const now = Date.now()
    
    // 更新用户记录
    const userRecord = this.userRecords.get(userId) || { count: 0, lastSendTime: 0 }
    userRecord.count++
    userRecord.lastSendTime = now
    this.userRecords.set(userId, userRecord)
    
    // 更新IP记录
    const ipRecord = this.ipRecords.get(ip) || { count: 0, lastSendTime: 0 }
    ipRecord.count++
    ipRecord.lastSendTime = now
    this.ipRecords.set(ip, ipRecord)
    
    // 更新全局记录
    const globalRecord = this.userRecords.get('global') || { count: 0, lastSendTime: 0 }
    globalRecord.count++
    globalRecord.lastSendTime = now
    this.userRecords.set('global', globalRecord)
  }

  // 检查是否可以发送
  public canSend(): { allowed: boolean, reason?: string, waitTime?: number } {
    const userId = this.generateUserFingerprint()
    const ip = this.getClientIP()
    
    // 检查用户限制
    const userCheck = this.checkUserLimit(userId)
    if (!userCheck.allowed) {
      return userCheck
    }
    
    // 检查IP限制
    const ipCheck = this.checkIPLimit(ip)
    if (!ipCheck.allowed) {
      return ipCheck
    }
    
    // 检查全局限制
    const globalCheck = this.checkGlobalLimit()
    if (!globalCheck.allowed) {
      return globalCheck
    }
    
    return { allowed: true }
  }

  // 清理过期记录
  public cleanup(): void {
    const now = Date.now()
    const maxAge = 24 * 60 * 60 * 1000 // 24小时
    
    // 清理用户记录
    for (const [key, record] of this.userRecords.entries()) {
      if (now - record.lastSendTime > maxAge) {
        this.userRecords.delete(key)
      }
    }
    
    // 清理IP记录
    for (const [key, record] of this.ipRecords.entries()) {
      if (now - record.lastSendTime > maxAge) {
        this.ipRecords.delete(key)
      }
    }
  }

  // 获取剩余等待时间
  public getRemainingWaitTime(): number {
    const userId = this.generateUserFingerprint()
    const ip = this.getClientIP()
    const now = Date.now()
    
    let maxWaitTime = 0
    
    // 检查用户限制
    const userRecord = this.userRecords.get(userId)
    if (userRecord?.blockedUntil && userRecord.blockedUntil > now) {
      maxWaitTime = Math.max(maxWaitTime, userRecord.blockedUntil - now)
    }
    
    // 检查IP限制
    const ipRecord = this.ipRecords.get(ip)
    if (ipRecord?.blockedUntil && ipRecord.blockedUntil > now) {
      maxWaitTime = Math.max(maxWaitTime, ipRecord.blockedUntil - now)
    }
    
    // 检查全局限制
    const globalRecord = this.userRecords.get('global')
    if (globalRecord?.blockedUntil && globalRecord.blockedUntil > now) {
      maxWaitTime = Math.max(maxWaitTime, globalRecord.blockedUntil - now)
    }
    
    return Math.ceil(maxWaitTime / 1000 / 60) // 返回分钟数
  }
}

// 创建单例实例
export const antiSpamManager = new AntiSpamManager()

// 定期清理过期记录
setInterval(() => {
  antiSpamManager.cleanup()
}, 60 * 60 * 1000) // 每小时清理一次
